/**
 * Iter276-labour Phase 2c — Labour Cost Model admin page.
 *
 * Path: /admin/labour-cost-model
 *
 * A per-region admin surface for the "RTR Business Labour Cost Model".
 * The math flows outward from this page into every recipe rollup and
 * quote pricing screen — so the safety gate is deliberately strict:
 *
 *   • Every change requires an admin session (backend `require_admin`)
 *   • On save, a modal asks the admin to type the exact phrase
 *     `UPDATE {REGION} MODEL` before the write is committed
 *   • Each save spawns a `labour_model_change_log` entry
 *   • Historical versions are visible + rollbackable (with the same
 *     typed-confirmation gate on the way in)
 *
 * Live-computed derived numbers (true hourly cost, charge hourly)
 * update as you edit — before you save — so the impact of each
 * tweak is immediately visible.
 *
 * Later phases plug into this same page:
 *   Phase 2b — "🔍 Review Labour Costs" (AI market snapshot + preset
 *              adjustments) will slot into the right column
 *   Phase 2c.5 — Historical business impact re-run panel (last 3/6/12
 *                months of closed quotes re-priced through the new
 *                model, shown in the confirmation dialog)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, RotateCcw, Loader2,
  Users, Wrench, Ruler, ShieldAlert, History, TrendingUp,
  Search, Sparkles, Check,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const REGION_LABEL = {
  hamilton: 'Hamilton', waikato_region: 'Waikato Region', cambridge: 'Cambridge',
  auckland: 'Auckland', tauranga: 'Tauranga',
  taupo: 'Taupō', wellington: 'Wellington', christchurch: 'Christchurch',
  other: 'Other',
};

export default function LabourCostModelAdminPage() {
  const { api } = useAuth();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState('hamilton');
  const [model, setModel] = useState(null);
  const [draft, setDraft] = useState(null); // in-progress edits
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGstInc, setShowGstInc] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // ─── Load regions + active model ────────────────────────────────
  const loadRegions = useCallback(async () => {
    try {
      const { data } = await api.get('/labour-cost-models/regions');
      setRegions(data.regions || []);
    } catch (err) {
      toast.error('Failed to load regions');
    }
  }, [api]);

  const loadModel = useCallback(async (r) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/labour-cost-models/active/${r}`);
      setModel(data.model);
      // Seed the editable draft from the loaded model (or an empty
      // template if no active model exists for that region yet).
      setDraft(data.model ? cloneModelForEdit(data.model) : emptyDraft());
    } catch (err) {
      toast.error(`Failed to load ${r} model`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadHistory = useCallback(async (r) => {
    try {
      const { data } = await api.get(`/labour-cost-models/history/${r}`);
      setHistory(data.models || []);
    } catch (err) {
      /* non-fatal */
    }
  }, [api]);

  useEffect(() => { loadRegions(); }, [loadRegions]);
  useEffect(() => { loadModel(region); loadHistory(region); }, [region, loadModel, loadHistory]);

  // ─── Live-computed derived numbers on the DRAFT ─────────────────
  const derivedDraft = useMemo(() => {
    if (!draft) return null;
    const oncost = Number(draft.employer_oncost_pct || 0);
    const profit = Number(draft.profit_margin_hourly || 0);
    return {
      ...draft,
      staff_tiers_computed: (draft.staff_tiers || []).map((t) => {
        const gross = Number(t.gross_hourly || 0);
        const over  = Number(t.overhead_hourly || 0);
        const trueCost = gross * (1 + oncost / 100) + over;
        return { ...t, true_cost_hourly: round2(trueCost), charge_hourly: round2(trueCost + profit) };
      }),
    };
  }, [draft]);

  const dirty = useMemo(() => JSON.stringify(model) !== JSON.stringify(draftToCanonical(draft)), [model, draft]);

  // ─── Field mutators ─────────────────────────────────────────────
  const setDraftField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const setStaffTier = (idx, key, value) => setDraft((d) => {
    const tiers = [...(d.staff_tiers || [])];
    tiers[idx] = { ...tiers[idx], [key]: key === 'label' || key === 'key' ? value : Number(value || 0) };
    return { ...d, staff_tiers: tiers };
  });
  const addStaffTier = () => setDraft((d) => ({
    ...d, staff_tiers: [...(d.staff_tiers || []), { key: 'new', label: 'New tier', gross_hourly: 0, overhead_hourly: 0 }],
  }));
  const removeStaffTier = (idx) => setDraft((d) => ({
    ...d, staff_tiers: (d.staff_tiers || []).filter((_, i) => i !== idx),
  }));

  const setContractorTier = (idx, key, value) => setDraft((d) => {
    const tiers = [...(d.contractor_tiers || [])];
    tiers[idx] = { ...tiers[idx], [key]: key === 'label' || key === 'key' ? value : Number(value || 0) };
    return { ...d, contractor_tiers: tiers };
  });
  const addContractorTier = () => setDraft((d) => ({
    ...d, contractor_tiers: [...(d.contractor_tiers || []), { key: 'new', label: 'New contractor tier', hourly: 0 }],
  }));
  const removeContractorTier = (idx) => setDraft((d) => ({
    ...d, contractor_tiers: (d.contractor_tiers || []).filter((_, i) => i !== idx),
  }));

  const setTradeBenchmark = (idx, key, value) => setDraft((d) => {
    const tb = [...(d.trade_rate_benchmarks || [])];
    tb[idx] = { ...tb[idx], [key]: key === 'meterage_rate' ? Number(value || 0) : value };
    return { ...d, trade_rate_benchmarks: tb };
  });
  const addTradeBenchmark = () => setDraft((d) => ({
    ...d, trade_rate_benchmarks: [...(d.trade_rate_benchmarks || []), { trade: 'new_trade', label: 'New trade', meterage_rate: 0, meterage_unit: '/m²' }],
  }));
  const removeTradeBenchmark = (idx) => setDraft((d) => ({
    ...d, trade_rate_benchmarks: (d.trade_rate_benchmarks || []).filter((_, i) => i !== idx),
  }));

  // ─── Save with type-to-confirm gate ─────────────────────────────
  const submitSave = async (confirmationText, notes) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/labour-cost-models/active/${region}`, {
        confirmation_text: confirmationText,
        source: 'manual',
        notes,
        model: draftToCanonical(draft),
      });
      setModel(data.model);
      setDraft(cloneModelForEdit(data.model));
      setShowConfirmModal(false);
      toast.success(`${REGION_LABEL[region]} model updated to v${data.model.version}`);
      loadHistory(region);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (!model) return;
    setDraft(cloneModelForEdit(model));
    toast.info('Reverted unsaved edits');
  };

  const rollback = async (targetVersion, confirmationText) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/labour-cost-models/rollback/${region}/${targetVersion}`,
        { confirmation_text: confirmationText },
      );
      setModel(data.model);
      setDraft(cloneModelForEdit(data.model));
      toast.success(`Rolled back to v${targetVersion} (new active version v${data.model.version})`);
      loadHistory(region);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Rollback failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const gstMultiplier = showGstInc ? 1.15 : 1;
  const $ = (n) => `$${(Number(n || 0) * gstMultiplier).toFixed(2)}`;

  if (loading || !draft) {
    return <div className="p-8 text-slate-500">Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="labour-cost-model-page">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1"
            data-testid="labour-cost-model-back"
          >
            <ArrowLeft size={12} /> Admin
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp size={20} className="text-violet-600" />
            Labour Cost Model
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Drives the &quot;true cost of labour&quot; math across every recipe rollup and quote.
            All rates stored <b>ex-GST</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-slate-300 bg-white cursor-pointer">
            <input
              type="checkbox"
              checked={showGstInc}
              onChange={(e) => setShowGstInc(e.target.checked)}
              data-testid="labour-cost-model-gst-toggle"
              className="accent-violet-600"
            />
            Show inc-GST (15%)
          </label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            data-testid="labour-cost-model-region-select"
            className="text-xs px-2 py-1.5 rounded-md border border-slate-300 bg-white"
          >
            {regions.map((r) => (
              <option key={r.key} value={r.key}>
                {REGION_LABEL[r.key] || r.key} {r.has_active_model ? '✓' : '· needs seeding'}
              </option>
            ))}
          </select>
          {dirty && (
            <button
              onClick={revert}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
              data-testid="labour-cost-model-revert-btn"
            >
              <RotateCcw size={12} /> Revert
            </button>
          )}
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={!dirty || saving}
            data-testid="labour-cost-model-save-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* ─── Model summary card (active version) ─── */}
      {model && (
        <div className="mb-4 border border-emerald-200 rounded-md bg-emerald-50/50 p-3 text-xs text-emerald-800 flex flex-wrap gap-4">
          <span>📌 <b>Active v{model.version}</b></span>
          <span>Effective from {new Date(model.effective_from).toLocaleDateString()}</span>
          <span>Source: {model.source}</span>
          <button
            onClick={() => setShowHistory((s) => !s)}
            className="ml-auto inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900"
            data-testid="labour-cost-model-history-toggle"
          >
            <History size={12} /> {showHistory ? 'Hide' : 'Show'} history ({history.length})
          </button>
        </div>
      )}

      {showHistory && (
        <HistoryPanel history={history} activeVersion={model?.version} region={region} onRollback={rollback} saving={saving} />
      )}

      {/* Iter276-labour Phase 2b — AI Market Research panel */}
      <AIMarketResearchPanel api={api} region={region} onModelApplied={() => { loadModel(region); loadHistory(region); }} />

      {/* ─── Staff tiers ─── */}
      <Section title="Staff Labour Tiers" icon={<Users size={16} className="text-violet-600" />} onAdd={addStaffTier} testid="staff-tiers">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 mb-1">
          <div className="col-span-3">Tier</div>
          <div className="col-span-2 text-right">Gross /hr</div>
          <div className="col-span-2 text-right">Overhead /hr</div>
          <div className="col-span-2 text-right">True cost /hr</div>
          <div className="col-span-2 text-right">Charge /hr</div>
          <div className="col-span-1"></div>
        </div>
        {(derivedDraft?.staff_tiers_computed || []).map((tier, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded p-2 mb-1" data-testid={`staff-tier-${i}`}>
            <input
              value={tier.label} onChange={(e) => setStaffTier(i, 'label', e.target.value)}
              className="col-span-3 text-xs border border-slate-200 rounded px-1.5 py-0.5"
              data-testid={`staff-tier-${i}-label`}
            />
            <NumberInput value={tier.gross_hourly} onChange={(v) => setStaffTier(i, 'gross_hourly', v)} testid={`staff-tier-${i}-gross`} />
            <NumberInput value={tier.overhead_hourly} onChange={(v) => setStaffTier(i, 'overhead_hourly', v)} testid={`staff-tier-${i}-overhead`} />
            <div className="col-span-2 text-right text-xs font-mono text-slate-700 tabular-nums">{$(tier.true_cost_hourly)}</div>
            <div className="col-span-2 text-right text-xs font-mono font-bold text-violet-700 tabular-nums">{$(tier.charge_hourly)}</div>
            <button onClick={() => removeStaffTier(i)} className="col-span-1 text-rose-500 hover:text-rose-700 justify-self-center" data-testid={`staff-tier-${i}-delete`}><Trash2 size={13} /></button>
          </div>
        ))}
      </Section>

      {/* ─── Employer on-cost ─── */}
      <Section title="Employer On-cost Loading" icon={<ShieldAlert size={16} className="text-orange-600" />} testid="oncost">
        <div className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3 text-xs">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Total on-cost %</label>
            <NumberInput value={draft.employer_oncost_pct} onChange={(v) => setDraftField('employer_oncost_pct', Number(v || 0))} testid="oncost-pct" wide />
          </div>
          <div className="col-span-9 text-[10px] text-slate-500 pl-2 border-l border-slate-200">
            Standard NZ trades ≈ 20%. Includes: KiwiSaver 3% · ACC ~2.5% · Annual leave 8% · Public holidays 4.2% · Sick leave 2.3%.
            <div className="mt-1 text-[10px] italic">Contractor rates below are quoted flat — contractors pay their own tax/leave, so no on-cost loading applies to them.</div>
          </div>
        </div>
      </Section>

      {/* ─── Profit margin ─── */}
      <Section title="Business Profit Margin on Labour" icon={<TrendingUp size={16} className="text-emerald-600" />} testid="profit">
        <div className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3 text-xs">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Profit margin $ /hr</label>
            <NumberInput value={draft.profit_margin_hourly} onChange={(v) => setDraftField('profit_margin_hourly', Number(v || 0))} testid="profit-margin" wide />
          </div>
          <div className="col-span-9 text-[10px] text-slate-500 pl-2 border-l border-slate-200">
            Flat $ added on top of the staff true hourly cost to produce the customer-facing charge rate.
            Your input range: <b>$30-$40/hr</b>. Applied to every staff tier.
          </div>
        </div>
      </Section>

      {/* ─── Contractor tiers ─── */}
      <Section title="Contractor Tiers" icon={<Wrench size={16} className="text-orange-600" />} onAdd={addContractorTier} testid="contractor-tiers">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 mb-1">
          <div className="col-span-4">Tier</div>
          <div className="col-span-6 text-right">Hourly rate (raw, ex-GST)</div>
          <div className="col-span-2"></div>
        </div>
        {(draft.contractor_tiers || []).map((tier, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded p-2 mb-1" data-testid={`contractor-tier-${i}`}>
            <input
              value={tier.label} onChange={(e) => setContractorTier(i, 'label', e.target.value)}
              className="col-span-4 text-xs border border-slate-200 rounded px-1.5 py-0.5"
              data-testid={`contractor-tier-${i}-label`}
            />
            <div className="col-span-6 flex justify-end">
              <NumberInput value={tier.hourly} onChange={(v) => setContractorTier(i, 'hourly', v)} testid={`contractor-tier-${i}-hourly`} wide />
            </div>
            <button onClick={() => removeContractorTier(i)} className="col-span-2 text-rose-500 hover:text-rose-700 justify-self-center" data-testid={`contractor-tier-${i}-delete`}><Trash2 size={13} /></button>
          </div>
        ))}
      </Section>

      {/* ─── Trade benchmarks ─── */}
      <Section title="Trade Rate Benchmarks" icon={<Ruler size={16} className="text-sky-600" />} onAdd={addTradeBenchmark} testid="trade-benchmarks">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 mb-1">
          <div className="col-span-3">Trade key</div>
          <div className="col-span-5">Label</div>
          <div className="col-span-2 text-right">Rate</div>
          <div className="col-span-1">Unit</div>
          <div className="col-span-1"></div>
        </div>
        {(draft.trade_rate_benchmarks || []).map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded p-2 mb-1" data-testid={`trade-benchmark-${i}`}>
            <input value={b.trade} onChange={(e) => setTradeBenchmark(i, 'trade', e.target.value)} className="col-span-3 text-xs border border-slate-200 rounded px-1.5 py-0.5 font-mono" data-testid={`trade-benchmark-${i}-key`} />
            <input value={b.label || ''} onChange={(e) => setTradeBenchmark(i, 'label', e.target.value)} className="col-span-5 text-xs border border-slate-200 rounded px-1.5 py-0.5" data-testid={`trade-benchmark-${i}-label`} />
            <div className="col-span-2 flex justify-end">
              <NumberInput value={b.meterage_rate} onChange={(v) => setTradeBenchmark(i, 'meterage_rate', v)} testid={`trade-benchmark-${i}-rate`} />
            </div>
            <input value={b.meterage_unit || '/m²'} onChange={(e) => setTradeBenchmark(i, 'meterage_unit', e.target.value)} className="col-span-1 text-xs border border-slate-200 rounded px-1.5 py-0.5" data-testid={`trade-benchmark-${i}-unit`} />
            <button onClick={() => removeTradeBenchmark(i)} className="col-span-1 text-rose-500 hover:text-rose-700 justify-self-center" data-testid={`trade-benchmark-${i}-delete`}><Trash2 size={13} /></button>
          </div>
        ))}
      </Section>

      {/* ─── Notes ─── */}
      <div className="mt-4">
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Notes (this version)</label>
        <textarea
          value={draft.notes || ''}
          onChange={(e) => setDraftField('notes', e.target.value)}
          rows={2}
          data-testid="labour-cost-model-notes"
          className="w-full border border-slate-300 rounded-md px-2 py-1 text-xs"
          placeholder="Reason for change, effective date rationale, etc."
        />
      </div>

      {/* ─── Confirmation modal ─── */}
      {showConfirmModal && (
        <ConfirmSaveModal
          region={region}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={submitSave}
          saving={saving}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function cloneModelForEdit(m) {
  const clone = JSON.parse(JSON.stringify(m || {}));
  // Strip derived fields — draft only carries raw inputs.
  delete clone.staff_tiers_computed;
  return clone;
}

function draftToCanonical(d) {
  if (!d) return null;
  const c = JSON.parse(JSON.stringify(d));
  delete c.staff_tiers_computed;
  return c;
}

function emptyDraft() {
  return {
    staff_tiers: [], employer_oncost_pct: 20, profit_margin_hourly: 35,
    contractor_tiers: [], trade_rate_benchmarks: [], notes: '',
  };
}

function round2(n) { return Math.round(n * 100) / 100; }

function Section({ title, icon, children, onAdd, testid }) {
  return (
    <div className="mb-4 border border-slate-200 rounded-md bg-slate-50/40 p-3" data-testid={`section-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">{icon}{title}</h2>
        {onAdd && (
          <button
            onClick={onAdd}
            data-testid={`${testid}-add-btn`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50"
          >
            <Plus size={11} /> Add
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, testid, wide }) {
  return (
    <input
      type="number"
      step="0.01"
      value={value ?? 0}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`${wide ? 'w-full' : 'col-span-2'} text-xs text-right border border-slate-200 rounded px-1.5 py-0.5 tabular-nums`}
    />
  );
}

function ConfirmSaveModal({ region, onClose, onConfirm, saving }) {
  const phrase = `UPDATE ${region.toUpperCase()} MODEL`;
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const matches = typed === phrase;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="confirm-save-modal" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
          <ShieldAlert size={16} className="text-rose-600" /> Confirm labour cost model update
        </h3>
        <p className="text-xs text-slate-600 mb-3">
          This change will propagate into every recipe rollup and quote priced under the <b>{region}</b> region.
          Type the phrase below to confirm.
        </p>
        <div className="bg-slate-100 rounded px-2 py-1 text-xs font-mono font-bold text-slate-900 mb-2">{phrase}</div>
        <input
          value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus
          placeholder={phrase}
          data-testid="confirm-save-modal-input"
          className={`w-full border rounded px-2 py-1.5 text-xs font-mono mb-2 focus:outline-none focus:ring-1 ${matches ? 'border-emerald-500 focus:ring-emerald-500 bg-emerald-50' : 'border-slate-300 focus:ring-violet-500'}`}
        />
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Notes (optional, saved in change log)</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder="e.g. Annual review, aligned with AI market snapshot, +5% premium…"
          data-testid="confirm-save-modal-notes"
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs mb-3"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50" data-testid="confirm-save-modal-cancel">Cancel</button>
          <button
            onClick={() => onConfirm(typed, notes)}
            disabled={!matches || saving}
            data-testid="confirm-save-modal-submit"
            className="text-xs px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-1"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save & apply
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryPanel({ history, activeVersion, region, onRollback, saving }) {
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [typed, setTyped] = useState('');
  const phrase = rollbackTarget ? `ROLLBACK ${region.toUpperCase()} MODEL` : '';
  return (
    <div className="mb-4 border border-slate-200 rounded-md bg-white p-3" data-testid="history-panel">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Change log · newest first</div>
      {history.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No prior versions.</div>
      ) : (
        <ul className="text-xs divide-y divide-slate-100">
          {history.map((h) => (
            <li key={h._id} className="py-1.5 flex items-center gap-2">
              <span className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1 rounded font-mono font-bold ${h.version === activeVersion ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>v{h.version}</span>
              <span className="text-slate-600">{new Date(h.effective_from).toLocaleString()}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500">{h.source || 'manual'}</span>
              {h.notes && <span className="text-slate-400 italic truncate">— {h.notes}</span>}
              {h.version !== activeVersion && (
                <button
                  onClick={() => { setRollbackTarget(h.version); setTyped(''); }}
                  className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                  data-testid={`history-rollback-btn-${h.version}`}
                >Roll back to v{h.version}</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {rollbackTarget !== null && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="rollback-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
              <ShieldAlert size={16} className="text-rose-600" /> Confirm rollback to v{rollbackTarget}
            </h3>
            <p className="text-xs text-slate-600 mb-3">Type the phrase to confirm the rollback.</p>
            <div className="bg-slate-100 rounded px-2 py-1 text-xs font-mono font-bold text-slate-900 mb-2">{phrase}</div>
            <input
              value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus
              data-testid="rollback-modal-input"
              className={`w-full border rounded px-2 py-1.5 text-xs font-mono mb-3 focus:outline-none focus:ring-1 ${typed === phrase ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}`}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRollbackTarget(null)} className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50" data-testid="rollback-modal-cancel">Cancel</button>
              <button
                disabled={typed !== phrase || saving}
                onClick={() => { onRollback(rollbackTarget, typed); setRollbackTarget(null); }}
                data-testid="rollback-modal-submit"
                className="text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
              >
                Roll back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AIMarketResearchPanel — provider selector, snapshot trigger,
// comparison viz, preset adjustments.
// ─────────────────────────────────────────────────────────────────
function AIMarketResearchPanel({ api, region, onModelApplied }) {
  const [providers, setProviders] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [running, setRunning] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [preset, setPreset] = useState('match');
  const [customPct, setCustomPct] = useState(0);
  const [preview, setPreview] = useState(null);
  const [impact, setImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [costSummary, setCostSummary] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-providers/available?task=market_research');
      setProviders(data.providers || []);
    } catch { /* ignore */ }
  }, [api]);

  const loadSnapshots = useCallback(async () => {
    try {
      const { data } = await api.get(`/labour-market-snapshots/${region}`);
      setSnapshots(data.snapshots || []);
      if ((data.snapshots || []).length > 0) setSelectedSnapshotId(data.snapshots[0]._id);
      else setSelectedSnapshotId(null);
    } catch { /* ignore */ }
  }, [api, region]);

  const loadCostSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/labour-market-snapshots-cost-summary?months=3');
      setCostSummary(data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { loadProviders(); loadCostSummary(); }, [loadProviders, loadCostSummary]);
  useEffect(() => { loadSnapshots(); setPreview(null); }, [loadSnapshots, region]);

  useEffect(() => {
    if (!selectedSnapshotId) { setSelectedSnapshot(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/labour-market-snapshots/detail/${selectedSnapshotId}`);
        setSelectedSnapshot(data.snapshot);
      } catch { /* ignore */ }
    })();
  }, [selectedSnapshotId, api]);

  const runReview = async (providerKey, useMock = false) => {
    setRunning(true);
    setShowProviderModal(false);
    try {
      const { data } = await api.post('/labour-cost-models/ai-review', {
        region, provider_key: providerKey, mock: useMock,
      });
      toast.success(useMock ? 'Snapshot generated (mock — no cost)' : 'Live AI research complete');
      await loadSnapshots();
      setSelectedSnapshotId(data.snapshot._id);
      loadCostSummary();
    } catch (err) {
      toast.error(`Review failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setRunning(false);
    }
  };

  const runPreview = useCallback(async () => {
    if (!selectedSnapshotId) return;
    setImpact(null);
    try {
      const { data } = await api.post('/labour-cost-models/preview-preset', {
        snapshot_id: selectedSnapshotId, preset,
        custom_pct: preset === 'custom' ? Number(customPct) : undefined,
      });
      setPreview(data);
      // Auto-fire impact preview once we have a projected model.
      if (data?.projected) {
        setLoadingImpact(true);
        try {
          const { data: imp } = await api.post('/business-metrics/impact-preview', {
            region,
            window_months: 12,
            projected_model: {
              staff_tiers: data.projected.staff_tiers,
              employer_oncost_pct: data.projected.employer_oncost_pct,
              profit_margin_hourly: data.projected.profit_margin_hourly,
            },
          });
          setImpact(imp);
        } catch (impErr) {
          // Non-fatal — leave impact null.
          console.warn('impact-preview failed', impErr);
        } finally {
          setLoadingImpact(false);
        }
      }
    } catch (err) {
      toast.error(`Preview failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, [api, selectedSnapshotId, preset, customPct, region]);

  const doApply = async (confirmationText, notes) => {
    setApplying(true);
    try {
      await api.post('/labour-cost-models/apply-preset', {
        snapshot_id: selectedSnapshotId, preset,
        custom_pct: preset === 'custom' ? Number(customPct) : undefined,
        confirmation_text: confirmationText, notes,
      });
      toast.success(`Applied preset "${preset}" — new active version created`);
      setShowApplyConfirm(false);
      onModelApplied?.();
    } catch (err) {
      toast.error(`Apply failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mb-4 border border-violet-200 rounded-lg bg-gradient-to-br from-violet-50 to-white p-3" data-testid="ai-market-research-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Sparkles size={16} className="text-violet-600" /> AI Market Research
        </h2>
        <div className="flex items-center gap-2">
          {costSummary && (
            <span className="text-[10px] text-slate-500" title="Rolling 3-month AI research spend">
              💰 ${costSummary.total_cost_nzd.toFixed(2)} · {costSummary.total_count} review{costSummary.total_count === 1 ? '' : 's'} (last 3mo)
            </span>
          )}
          <button
            onClick={() => setShowProviderModal(true)}
            disabled={running}
            data-testid="ai-review-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            {running ? 'Researching…' : `🔍 Review ${REGION_LABEL[region] || region}`}
          </button>
        </div>
      </div>

      {snapshots.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3 text-center">
          No snapshots yet for {REGION_LABEL[region] || region}. Click <b>Review</b> to generate the first (mock) research pass.
        </div>
      ) : (
        <>
          <div className="mb-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Snapshot:</label>
            <select
              value={selectedSnapshotId || ''}
              onChange={(e) => setSelectedSnapshotId(e.target.value)}
              data-testid="ai-snapshot-select"
              className="ml-2 text-xs px-2 py-1 rounded border border-slate-300 bg-white"
            >
              {snapshots.map((s) => (
                <option key={s._id} value={s._id}>
                  {new Date(s.generated_at).toLocaleString()} · {s.llm_provider}{s.is_mock ? ' (mock)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedSnapshot && (
            <>
              <ComparisonBars snapshot={selectedSnapshot} />

              <div className="mt-3 pt-3 border-t border-violet-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Preset Adjustment</div>
                <div className="flex flex-wrap gap-2 items-center">
                  {[['match', 'Match'], ['premium_5', '+5%'], ['premium_10', '+10%'], ['premium_15', '+15%'], ['premium_20', '+20%'], ['custom', 'Custom']].map(([k, l]) => (
                    <label key={k} className={`text-xs px-2 py-1 rounded border cursor-pointer ${preset === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`} data-testid={`preset-${k}`}>
                      <input type="radio" checked={preset === k} onChange={() => setPreset(k)} className="hidden" />
                      {l}
                    </label>
                  ))}
                  {preset === 'custom' && (
                    <input type="number" step="1" value={customPct} onChange={(e) => setCustomPct(e.target.value)}
                      data-testid="preset-custom-pct"
                      className="w-16 text-xs border border-slate-300 rounded px-1 py-0.5" placeholder="%" />
                  )}
                  <button onClick={runPreview} data-testid="preset-preview-btn"
                    className="ml-2 text-xs px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1">
                    <Sparkles size={11} /> Preview
                  </button>
                  {preview && (
                    <button onClick={() => setShowApplyConfirm(true)} data-testid="preset-apply-btn"
                      className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                      <Check size={11} /> Apply → Save
                    </button>
                  )}
                </div>
              </div>

              {preview && preview.projected && (
                <div className="mt-3 bg-white border border-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Projected model preview</div>
                  <div className="text-xs grid grid-cols-3 gap-1">
                    {preview.projected.staff_tiers_computed?.map((t, i) => (
                      <div key={i} className="p-1.5 bg-slate-50 rounded">
                        <div className="text-[10px] text-slate-500">{t.label}</div>
                        <div className="text-xs font-bold">gross <span className="text-violet-700 tabular-nums">${t.gross_hourly}</span></div>
                        <div className="text-[10px] text-slate-500">charge <span className="text-slate-800 tabular-nums">${t.charge_hourly}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ImpactDrawer impact={impact} loading={loadingImpact} region={region} />
            </>
          )}
        </>
      )}

      {showProviderModal && (
        <ProviderSelectModal providers={providers} onClose={() => setShowProviderModal(false)} onSelect={runReview} />
      )}
      {showApplyConfirm && (
        <ApplyPresetConfirmModal region={region} preset={preset} onClose={() => setShowApplyConfirm(false)} onConfirm={doApply} applying={applying} />
      )}
    </div>
  );
}

function ImpactDrawer({ impact, loading, region }) {
  if (loading) {
    return (
      <div className="mt-3 border border-amber-200 bg-amber-50 rounded p-2 text-xs text-slate-600 flex items-center gap-2" data-testid="impact-drawer-loading">
        <Loader2 size={12} className="animate-spin" /> Estimating 12-month impact for {region}…
      </div>
    );
  }
  if (!impact) return null;
  const deltaAll = impact.labour_delta_all_jobs || 0;
  const deltaAcc = impact.labour_delta_accepted_only || 0;
  const positive = deltaAll >= 0;
  return (
    <div className="mt-3 border border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded p-2" data-testid="impact-drawer">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
          📊 12-month impact preview · {region}
        </div>
        <div className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${positive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
          {positive ? '+' : ''}{impact.delta_pct}% avg charge
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5 text-xs">
        <div className="p-1.5 bg-white rounded border border-slate-100">
          <div className="text-[10px] text-slate-500">Jobs quoted</div>
          <div className="text-sm font-bold text-slate-900 tabular-nums">{impact.n_jobs}</div>
        </div>
        <div className="p-1.5 bg-white rounded border border-slate-100">
          <div className="text-[10px] text-slate-500">Accepted</div>
          <div className="text-sm font-bold text-emerald-700 tabular-nums">{impact.n_accepted} <span className="text-[9px] text-slate-500 font-normal">({impact.conversion_rate_pct}%)</span></div>
        </div>
        <div className="p-1.5 bg-white rounded border border-slate-100" data-testid="impact-current-hourly">
          <div className="text-[10px] text-slate-500">Current avg $/hr</div>
          <div className="text-sm font-bold text-slate-800 tabular-nums">${impact.current_avg_charge_hourly}</div>
        </div>
        <div className="p-1.5 bg-white rounded border border-slate-100" data-testid="impact-projected-hourly">
          <div className="text-[10px] text-slate-500">Projected avg $/hr</div>
          <div className="text-sm font-bold text-violet-700 tabular-nums">${impact.projected_avg_charge_hourly}</div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
        <div className="p-1.5 bg-white rounded border border-slate-100" data-testid="impact-delta-all">
          <div className="text-[10px] text-slate-500">Labour Δ · all jobs</div>
          <div className={`text-sm font-bold tabular-nums ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
            {positive ? '+' : ''}${deltaAll.toLocaleString()}
          </div>
          <div className="text-[9px] text-slate-500">
            ${impact.labour_current_total.toLocaleString()} → ${impact.labour_projected_total.toLocaleString()}
          </div>
        </div>
        <div className="p-1.5 bg-white rounded border border-slate-100" data-testid="impact-delta-accepted">
          <div className="text-[10px] text-slate-500">Labour Δ · accepted only</div>
          <div className={`text-sm font-bold tabular-nums ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
            {positive ? '+' : ''}${deltaAcc.toLocaleString()}
          </div>
          <div className="text-[9px] text-slate-500">
            Total accepted value: ${impact.accepted_value_ex_gst.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="mt-1.5 text-[9px] italic text-slate-500">{impact.notes}</div>
    </div>
  );
}

function ComparisonBars({ snapshot }) {
  const rows = [
    ...(snapshot.staff_tiers || []).map((t) => ({ ...t, kind: 'staff' })),
    ...(snapshot.contractor_tiers || []).map((t) => ({ ...t, kind: 'contractor' })),
  ];
  return (
    <div className="space-y-1 text-xs">
      {rows.map((r, i) => {
        const range = (r.market_high || 0) - (r.market_low || 0);
        const avgPos = range > 0 ? (((r.market_avg || 0) - (r.market_low || 0)) / range) * 100 : 50;
        return (
          <div key={i} className="flex items-center gap-2" data-testid={`market-bar-${r.kind}-${r.key}`}>
            <div className="w-32 truncate text-slate-700">{r.label}</div>
            <div className="flex-1 h-4 bg-slate-100 rounded relative">
              <div className="absolute inset-y-0 bg-violet-200 rounded" style={{ left: 0, right: 0 }} />
              <div className="absolute inset-y-0 w-0.5 bg-violet-600" style={{ left: `${avgPos}%` }} title={`avg $${r.market_avg}`} />
            </div>
            <div className="w-40 text-[10px] font-mono tabular-nums text-slate-600 text-right">
              ${r.market_low} · <b className="text-violet-700">${r.market_avg}</b> · ${r.market_high}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProviderSelectModal({ providers, onClose, onSelect }) {
  const [useMock, setUseMock] = useState(false);
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="provider-modal" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Sparkles size={16} className="text-violet-600" /> Choose AI Provider
        </h3>
        <label className="flex items-center gap-2 mb-3 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5 cursor-pointer">
          <input type="checkbox" checked={useMock} onChange={(e) => setUseMock(e.target.checked)} data-testid="ai-review-mock-toggle" />
          <span><b>Mock mode</b> — deterministic dummy data, no LLM credits spent. Uncheck for live web-grounded research.</span>
        </label>
        <div className="space-y-2">
          {providers.map((p) => (
            <button key={p.key} onClick={() => onSelect(p.key, useMock)} data-testid={`provider-select-${p.key}`}
              className={`w-full text-left p-2 rounded border ${p.recommended ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'} hover:border-violet-500`}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="text-sm font-bold text-slate-900">{p.recommended && '🌟 '}{p.label}</div>
                <div className="text-xs font-mono text-slate-500">{useMock ? 'FREE (mock)' : `~$${p.estimated_cost_nzd.toFixed(2)}`}</div>
              </div>
              <div className="text-[10px] text-slate-600 mb-0.5">{p.vendor} · {p.capabilities.join(' · ')}</div>
              <div className="text-[10px] text-slate-500 italic">{p.notes}</div>
            </button>
          ))}
        </div>
        <button onClick={onClose} className="mt-3 text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
}

function ApplyPresetConfirmModal({ region, preset, onClose, onConfirm, applying }) {
  const phrase = `UPDATE ${region.toUpperCase()} MODEL`;
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
          <ShieldAlert size={16} className="text-rose-600" /> Apply preset · {preset}
        </h3>
        <p className="text-xs text-slate-600 mb-3">This will create a new active version for <b>{region}</b>. Type the phrase to confirm.</p>
        <div className="bg-slate-100 rounded px-2 py-1 text-xs font-mono font-bold mb-2">{phrase}</div>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus data-testid="apply-preset-confirm-input"
          className={`w-full border rounded px-2 py-1.5 text-xs font-mono mb-2 ${typed === phrase ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}`} />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)"
          data-testid="apply-preset-notes"
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs mb-3" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white">Cancel</button>
          <button disabled={typed !== phrase || applying} onClick={() => onConfirm(typed, notes)} data-testid="apply-preset-submit"
            className="text-xs px-3 py-1.5 rounded bg-rose-600 text-white disabled:opacity-40 inline-flex items-center gap-1">
            {applying ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Apply
          </button>
        </div>
      </div>
    </div>
  );
}

